<template>
  <div>
    <!-- mobile offcanvas trigger -->
    <div class="d-lg-none mb-3 text-end">
      <button class="btn btn-outline-secondary btn-sm" type="button" @click="mobileOpen = true">
        <i class="fas fa-filter"></i>
        <span v-if="selectedTagIds.length" class="badge bg-primary ms-1">{{ selectedTagIds.length }}</span>
      </button>
    </div>

    <!-- offcanvas -->
    <div
      class="offcanvas offcanvas-end"
      :class="{ show: mobileOpen }"
      :style="mobileOpen ? 'visibility:visible;' : 'visibility:hidden;'"
      tabindex="-1"
    >
      <div class="offcanvas-header">
        <h5 class="offcanvas-title text-secondary"><i class="fas fa-filter"></i></h5>
        <button type="button" class="btn-close" @click="mobileOpen = false"></button>
      </div>
      <div class="offcanvas-body tag-filters">
        <div v-for="group in categorisedTagList" :key="group.category" class="mb-3">
          <p class="fw-semibold small text-muted mb-1">{{ group.category }}</p>
          <div v-for="tag in group.tags" :key="tag.id" class="form-check mb-3">
            <input
              class="form-check-input"
              type="checkbox"
              :id="`tag-offcanvas-${tag.id}`"
              :value="tag.id"
              v-model="selectedTagIds"
            />
            <label
              class="form-check-label d-flex justify-content-between align-items-center"
              :for="`tag-offcanvas-${tag.id}`"
            >
              <span>{{ tag.name }}</span>
              <span class="badge bg-secondary bg-opacity-25 text-dark ms-2">
                {{ tag.courses_published ? tag.courses_published.length : 0 }}
              </span>
            </label>
          </div>
        </div>
      </div>
    </div>
    <div v-if="mobileOpen" class="offcanvas-backdrop fade show d-lg-none" @click="mobileOpen = false"></div>

    <div class="row g-5">
      <!-- desktop sidebar -->
      <div class="col-3 d-none d-lg-block border-end tag-filters">
        <div v-for="group in categorisedTagList" :key="group.category" class="mb-4">
          <p class="fw-semibold mb-2">{{ group.category }}</p>
          <ul class="list-unstyled">
            <li v-for="tag in group.tags" :key="tag.id" class="mb-2">
              <div class="form-check small">
                <input
                  class="form-check-input"
                  type="checkbox"
                  :id="`tag-${tag.id}`"
                  :value="tag.id"
                  v-model="selectedTagIds"
                />
                <label
                  class="form-check-label d-flex justify-content-between align-items-center"
                  :for="`tag-${tag.id}`"
                >
                  <span>{{ tag.name }}</span>
                  <span class="badge bg-secondary bg-opacity-10 text-dark ms-2">
                    {{ tag.courses_published ? tag.courses_published.length : 0 }}
                  </span>
                </label>
              </div>
            </li>
          </ul>
        </div>
      </div>
      <div class="col-12 col-lg-9">
        <div class="row row-cols-1 row-cols-lg-3 g-3">
          <div class="col my-3 course user-select-none" v-for="product in filteredProducts" :key="product.id">
            <template v-if="displayAsCard">
              <div class="card border-0 rounded-3 h-100">
                <a v-if="!hasButtons" :href="`/${product.slug}`" class="stretched-link" :aria-label="product.title"></a>

                <img
                  v-if="product.cover_photo"
                  :src="product.cover_photo"
                  :alt="product.title"
                  class="card-image-top rounded-top-3"
                  loading="lazy"
                />
                <img
                  v-else
                  class="card-image-top rounded-top-3"
                  src="https://placehold.co/1280x800/E1D4F1/ffffff"
                  alt=""
                  loading="lazy"
                />

                <div class="card-body">
                  <h6 class="card-title text-black fw-bold small">{{ product.title }}</h6>

                  <template v-if="product.description">
                    <p v-for="(line, i) in product.description.split('\n')" :key="i" class="card-text small">
                      {{ line }}
                    </p>
                  </template>

                  <p v-if="product.difficulty" class="card-text small">{{ product.difficulty }}</p>

                  <div v-if="!doNotDisplayPrices">
                    <template v-if="product.is_free && !product.allow_student_to_set_price">
                      <span class="text-black free fw-bold">{{ 'Free' | translate }}</span>
                    </template>
                    <template v-else-if="product.is_free && product.allow_student_to_set_price">
                      <span class="text-black">{{ 'Set your price' | translate }}</span>
                    </template>
                    <template v-else-if="!product.is_free && product.allow_student_to_set_price">
                      <span class="text-black fw-bold">
                        {{ 'From' | translate }} {{ formatCurrency(product.current_price) }}
                      </span>
                      |
                      <span class="text-black">{{ 'Set your price' | translate }}</span>
                    </template>
                    <template v-else-if="product.price_variants">
                      <span class="text-black fw-bold">{{ 'Price variants' | translate }}</span>
                    </template>
                    <template v-else-if="product.current_price">
                      <span class="text-black fw-bold">{{ formatCurrency(product.current_price) }}</span>
                      <small v-if="product.has_promo" class="text-muted ms-1">
                        <del>{{ formatCurrency(product.price) }}</del>
                      </small>
                    </template>
                  </div>

                  <div v-if="hasButtons" class="text-center mb-3 mt-3">
                    <a v-if="displayMoreButton" :class="['btn', 'btn-' + moreButtonStyle]" :href="`/${product.slug}`">
                      {{ moreButtonText | translate }}
                    </a>
                    <a
                      v-if="displayBuyButton"
                      :class="['btn', 'btn-' + buyButtonStyle]"
                      :href="`/${product.slug}/checkout`"
                    >
                      {{ buyButtonText | translate }}
                    </a>
                  </div>
                </div>
              </div>
            </template>

            <!-- element style -->
            <template v-else>
              <div class="course-hover-shadow position-relative rounded h-100">
                <a v-if="!hasButtons" :href="`/${product.slug}`" class="stretched-link" :aria-label="product.title"></a>

                <img
                  :src="imgUrl(product.cover_photo)"
                  :alt="product.title"
                  width="1280"
                  height="800"
                  class="img-fluid rounded mb-3"
                  loading="lazy"
                />

                <div class="mb-2">
                  <h6 class="text-black mb-1 small">{{ product.title }}</h6>
                  <template v-if="product.description">
                    <p v-for="(line, i) in product.description.split('\n')" :key="i" class="text-muted mb-1 small">
                      {{ line }}
                    </p>
                  </template>
                </div>

                <div>
                  <div v-if="product.difficulty" class="text-muted small">{{ product.difficulty }}</div>

                  <div v-if="!doNotDisplayPrices">
                    <template v-if="product.is_free && !product.allow_student_to_set_price">
                      <span class="text-black free">{{ 'Free' | translate }}</span>
                    </template>
                    <template v-else-if="product.is_free && product.allow_student_to_set_price">
                      <span class="text-black">{{ 'Set your price' | translate }}</span>
                    </template>
                    <template v-else-if="!product.is_free && product.allow_student_to_set_price">
                      <span class="text-black">
                        {{ 'From' | translate }} {{ formatCurrency(product.current_price) }} |
                      </span>
                      <span class="text-black">{{ 'Set your price' | translate }}</span>
                    </template>
                    <template v-else-if="product.price_variants">
                      <span class="text-black">{{ 'Price variants' | translate }}</span>
                    </template>
                    <template v-else-if="product.current_price">
                      <span class="text-black">{{ formatCurrency(product.current_price) }}</span>
                      <small v-if="product.has_promo" class="text-muted">
                        <del>{{ formatCurrency(product.price) }}</del>
                      </small>
                    </template>
                  </div>

                  <div v-if="hasButtons" class="text-center mt-3 position-relative" style="z-index: 2">
                    <a v-if="displayMoreButton" :class="['btn', 'btn-' + moreButtonStyle]" :href="`/${product.slug}`">
                      {{ moreButtonText | translate }}
                    </a>
                    <a
                      v-if="displayBuyButton"
                      :class="['btn', 'btn-' + buyButtonStyle]"
                      :href="`/${product.slug}/checkout`"
                    >
                      {{ buyButtonText | translate }}
                    </a>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    tags: { type: Array, default: () => [] },
    products: { type: Array, default: () => [] },
    currency: { type: String, default: '' },
    doNotDisplayPrices: { type: Boolean, default: false },
    displayAsCard: { type: Boolean, default: false },
    displayMoreButton: { type: Boolean, default: false },
    displayBuyButton: { type: Boolean, default: false },
    moreButtonStyle: { type: String, default: 'primary' },
    moreButtonText: { type: String, default: 'More' },
    buyButtonStyle: { type: String, default: 'primary' },
    buyButtonText: { type: String, default: 'Buy now' },
  },
  data() {
    return {
      selectedTagIds: [],
      mobileOpen: false,
    }
  },
  computed: {
    categorisedTagList() {
      const categories = this.tagCategories
      const tagsWithCourses = this.tags.filter((t) => t.courses_published?.length)
      if (!categories) {
        return [{ category: '', tags: tagsWithCourses }]
      }
      const assignedTagNames = new Set(Object.values(categories).flat())
      const tagsByName = Object.fromEntries(tagsWithCourses.map((t) => [t.name, t]))
      const groups = Object.entries(categories).map(([category, names]) => ({
        category,
        tags: names.map((name) => tagsByName[name]).filter(Boolean),
      }))
      const uncategorised = tagsWithCourses.filter((t) => !assignedTagNames.has(t.name))
      if (uncategorised.length) {
        groups.push({ category: this.$t('Other'), tags: uncategorised })
      }
      return groups.filter((g) => g.tags.length > 0)
    },
    tagCategories() {
      return window.tagCategories || null
    },
    hasButtons() {
      return this.displayMoreButton || this.displayBuyButton
    },
    filteredProducts() {
      if (this.selectedTagIds.length === 0) {
        return this.products
      }

      // Group selected tag ids by category
      const selectedByCategory = {}
      for (const group of this.categorisedTagList) {
        const ids = group.tags.map((t) => t.id).filter((id) => this.selectedTagIds.includes(id))
        if (ids.length) {
          selectedByCategory[group.category] = ids
        }
      }

      // For each active category, collect the product ids that match ANY tag in it (OR within category)
      // Then intersect across categories (AND across categories)
      const productIdSets = Object.values(selectedByCategory).map((tagIds) => {
        const selectedTags = this.tags.filter((t) => tagIds.includes(t.id))
        return new Set(selectedTags.flatMap((tag) => (tag.courses_published || []).map((p) => p.id)))
      })

      return this.products.filter((p) => productIdSets.every((set) => set.has(p.id)))
    },
  },
  methods: {
    imgUrl(url) {
      if (url) {
        return `https://djceg34j53tq.cloudfront.net/${url}`
      }
      return 'https://placehold.co/1280x800/E1D4F1/ffffff'
    },
    formatCurrency(amount) {
      const value = parseInt(amount) / 100
      const currency = this.currency
      if (!currency) return value.toString()
      return value.toLocaleString(document.documentElement.lang, {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
      })
    },
  },
}
</script>

<style scoped>
.tag-filters .form-check-input {
  border-color: #000;
}
.tag-filters .form-check-input:checked {
  background-color: #000;
  border-color: #000;
}
.tag-filters .form-check-input:focus {
  border-color: #000;
  box-shadow: 0 0 0 0.25rem rgba(0, 0, 0, 0.25);
}
</style>
